/** Auto-dismiss timer for widget states. Hover pauses, mouse leave resumes. */

import { useCallback, useEffect, useRef, useState } from 'react';
import { type WidgetState } from '../dictation-widget.types';

interface UseWidgetAutoHideOptions {
  state: WidgetState;
  onHide: () => void;
  /**
   * SCR-6773: when true, skip the timer entirely (e.g. flag-on no-audio
   * recovery surface stays open until the user closes it).
   */
  suppressAutoHide?: boolean;
  /**
   * Overrides the default auto-hide delay for the current auto-hiding state.
   * The post-dictation feedback prompt needs longer than RESULT's 3s to read
   * the question and click a thumb, so it passes the 15s window. Ignored when
   * the state does not auto-hide or `suppressAutoHide` is set.
   */
  autoHideMsOverride?: number | null;
}

export const AUTO_HIDE_MS = 3000;
export const SIGN_IN_PROMPT_AUTO_HIDE_MS = 10_000;
export const FEEDBACK_PROMPT_AUTO_HIDE_MS = 15_000;
export const FEEDBACK_CONFIRMATION_AUTO_HIDE_MS = 5000;

function getAutoHideMs(
  state: WidgetState,
  suppressAutoHide: boolean | undefined,
  autoHideMsOverride: number | null | undefined
): number | null {
  if (suppressAutoHide) return null;
  const base =
    state === 'SIGN_IN_PROMPT'
      ? SIGN_IN_PROMPT_AUTO_HIDE_MS
      : state === 'RESULT' || state === 'ERROR' || state === 'INSERTION_FALLBACK'
        ? AUTO_HIDE_MS
        : null;
  if (base === null) return null;
  return autoHideMsOverride ?? base;
}

/** Returns mouse event handlers for hover pause/resume plus auto-hide status. */
export function useWidgetAutoHide({
  state,
  onHide,
  suppressAutoHide,
  autoHideMsOverride,
}: UseWidgetAutoHideOptions) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoveredRef = useRef(false);
  const [hovered, setHovered] = useState(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    const autoHideMs = getAutoHideMs(state, suppressAutoHide, autoHideMsOverride);
    if (autoHideMs === null) return;

    clearTimer();
    timerRef.current = setTimeout(() => {
      if (hoveredRef.current) return;
      onHide();
    }, autoHideMs);
  }, [clearTimer, onHide, state, suppressAutoHide, autoHideMsOverride]);

  // Start/stop timer based on state
  useEffect(() => {
    const shouldAutoHide =
      getAutoHideMs(state, suppressAutoHide, autoHideMsOverride) !== null;
    if (shouldAutoHide && !hovered) {
      startTimer();
    } else {
      clearTimer();
    }

    return clearTimer;
  }, [state, hovered, startTimer, clearTimer, suppressAutoHide, autoHideMsOverride]);

  const onMouseEnter = useCallback(() => {
    hoveredRef.current = true;
    setHovered(true);
  }, []);

  const onMouseMove = useCallback(() => {
    if (!hoveredRef.current) {
      hoveredRef.current = true;
      setHovered(true);
    }
  }, []);

  const onMouseLeave = useCallback(() => {
    hoveredRef.current = false;
    setHovered(false);
  }, []);

  const isAutoHiding =
    getAutoHideMs(state, suppressAutoHide, autoHideMsOverride) !== null;

  return { onMouseEnter, onMouseMove, onMouseLeave, isAutoHiding, hovered };
}
